/**
 * @file
 * Adapted -- A11y Responsive Tabs (vanilla js).
 * @see https://codepen.io/BeyondHyper/pen/xZXXzj
 *
 * This tab solution is based on Scott Vinkle's Simple Tabs with some
 * modifications to remove the jQuery dependency, as well as modifying the UI
 * look and feel.
 * @see https://codepen.io/svinkle/pen/edmDF
 *
 * !AH NOTE: Do not add an enable variable (window.drupalSettings.hasTabWidget)
 * since the tabs widget JS is used by other widgets and components where they
 * might just add the tabs classes to their markup. Also, the tabs widget is
 * used for modal popup content on the infographic map widget.
 */

(function tabWidgetScript($, Drupal, once) {
  'use strict';

  // Scroll top (without focus).
  function scrollTop(target, topBuffer) {
    Drupal.ahsThemeSite.scrollTop(target, {
      topBuffer: topBuffer,
      noFocus: true,
      duration: 250,
    });
  }

  function selectLink(link) {
    link.setAttribute('tabindex', '0');
    link.setAttribute('aria-selected', 'true');
    link.parentNode.setAttribute('data-tab-active', '');
    link.setAttribute('data-tab-active', '');
  }

  function unselectLink(link) {
    link.setAttribute('tabindex', '-1');
    link.setAttribute('aria-selected', 'false');
    link.parentNode.removeAttribute('data-tab-active');
    link.removeAttribute('data-tab-active');
  }

  function showPanel(panel) {
    panel.setAttribute('aria-hidden', 'false');
    panel.setAttribute('tabindex', '0');
    panel.setAttribute('data-tab-active', '');
  }

  function hidePanel(panel) {
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('tabindex', '-1');
    panel.removeAttribute('data-tab-active');
  }

  // Update the slider based on the active item.
  function updateSlider($tabSlider, $activeItem) {
    if ($tabSlider.length > 0 && $activeItem.length > 0) {
      const sliderWidth = $activeItem.outerWidth();

      // Update only if the active item has a width.
      // If the tab widget is hidden, then the active item will have a width
      // of 0. Example: Infographic Map widget modal content.
      if (sliderWidth > 0) {
        const sliderPosition = $activeItem.position();
        if ('left' in sliderPosition) {
          $tabSlider.css(
            {
              left: +sliderPosition.left,
              width: sliderWidth,
            },
          );
        }
      }
    }
  }

  // Release the tab click event lock.
  function releaseTabClickEvent(link, panel) {
    const $link = $(link);
    if ($link.length > 0) {
      $link.data('tabClickEventLocked', false);

      // Trigger tab selected event after click processing.
      $link.trigger('ahsThemeTabSelected', [{
        panel: panel,
      }]);
    }
  }

  // Returns true if the tab click event was locked.
  function lockTabClickEvent(tabLink) {
    const $tabLink = $(tabLink);
    if ($tabLink.length > 0) {
      // Skip.
      if ($tabLink.data('tabClickEventLocked') === true) {
        return false;
      }

      // Set flag.
      $tabLink.data('tabClickEventLocked', true);
      return true;
    }

    return false;
  }

  function tabClickEvent(e, tabLinks, tabPanels, tabSlider, linkIndex) {
    const tabLink = e.currentTarget;
    let activePanel;
    e.preventDefault();
    // Exit if already selected or click event is processing.
    if (tabLink.getAttribute('aria-selected') === 'true' || !lockTabClickEvent(tabLink)) {
      return;
    }

    // Reset all the tablinks.
    tabLinks.forEach((link) => {
      unselectLink(link);
    });

    // Set the active link attributes.
    selectLink(tabLink);

    // Change tab panel visibility.
    tabPanels.forEach((panel, index) => {
      if (index !== linkIndex) {
        hidePanel(panel);
      } else {
        showPanel(panel);
        activePanel = panel;
      }
    });

    // Move the Slider.
    updateSlider($(tabSlider), $(tabLink).parent('.tab-item'));

    // Clear the 'is clicking' flag.
    releaseTabClickEvent(tabLink, activePanel);
  }

  function keyboardEvent(e, tabLinks, tabPanels, tabItems, index) {
    const previousTab = tabLinks[index - 1];
    const nextTab = tabLinks[index + 1];
    const firstTab = tabLinks[0];
    const lastTab = tabLinks[tabLinks.length - 1];

    // ArrowRight and ArrowLeft are the values when event.key is supported.
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();

        if (!previousTab) {
          lastTab.focus();
        } else {
          previousTab.focus();
        }
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();

        if (!nextTab) {
          firstTab.focus();
        } else {
          nextTab.focus();
        }
        break;

      default:
        break;
    }
  }

  function windowResizeEvent(tabWidgetInstance) {
    const $thisTabWidgetInstance = $(tabWidgetInstance);
    const $tabList = $thisTabWidgetInstance.find('.tab-list');

    // Find active Item.
    const $activeItem = $tabList.find('.tab-item').find('[data-tab-active]');

    if ($activeItem.length > 0) {
      // Adjust slider width.
      const $tabSlider = $tabList.children('.tab-slider');
      updateSlider($tabSlider, $activeItem);
    }
  }

  function tabAccordionClickEvent(
    e,
    tabLinks,
    tabPanels,
    tabAccordionControls,
  ) {
    const tabAccordionControl = e.currentTarget;
    let activePanel;
    e.preventDefault();

    // Determine if this links panel is already open
    let isActivePanel = false;
    if (tabAccordionControl.nextElementSibling.hasAttribute('data-tab-active')) {
      isActivePanel = true;
    }

    // Reset all the tablinks.
    tabLinks.forEach((link) => {
      unselectLink(link);
    });

    // Reset accordion links
    tabAccordionControls.forEach((link) => {
      link.removeAttribute('data-tab-active');
    });

    tabPanels.forEach((panel) => {
      hidePanel(panel);
    });

    // If the panel wasn't active, make it active
    if (!isActivePanel) {
      tabAccordionControl.setAttribute('data-tab-active', '');
      const tabIndex = tabAccordionControl.getAttribute('data-tab-index');
      // Get the matching tabLink,
      // set it's active attributes.
      tabLinks.forEach((tabLink) => {
        if (tabLink.getAttribute('data-tab-index') === tabIndex) {
          selectLink(tabLink);
        }
      });

      // Change tab panel visibility.
      tabPanels.forEach((panel) => {
        if (panel.getAttribute('data-tab-index') === tabIndex) {
          showPanel(panel);
          activePanel = panel;
          scrollTop($(panel), 175);
        }
      });
    }

    // Clear the 'is clicking' flag.
    releaseTabClickEvent(tabAccordionControl, activePanel);
  }

  // set up tab widgets
  function init(tabWidgetInstance) {
    const $tabWidgetInstance = $(tabWidgetInstance);
    const tabWidgetId = tabWidgetInstance.id;
    const $tabList = $tabWidgetInstance
      .find('.tab-list')
      .first();
    const $tabItems = $tabList.find('.tab-item');
    const tabItems = $tabItems.get();
    const tabAccordionControls = $tabWidgetInstance.find('.tabs-accordion-control').get();
    const tabPanels = $tabWidgetInstance.find('.tab-panel').get();
    const $tabSlider = $tabList.children('.tab-slider');
    const tabSlider = $tabSlider.length > 0 ? $tabSlider.get(0) : undefined;
    const tabLinks = [];
    let hash = window.location.hash;
    let activeTabId = null;
    let activeItem = null;
    let $activeItem = null;
    let topItem = null;

    if (hash.length > 0 && hash.indexOf('#') === 0) {
      hash = hash.substring(1);
    }

    // Add accessibility roles and labels
    $tabList.attr('role', 'tablist');
    $tabItems.get().forEach((item) => {
      const $item = $(item);
      const $link = $item.children('.tab-link').first();
      const anchor = $link.attr('href').split('#')[1];

      tabLinks.push($link.get(0));
      $item.attr('role', 'presentation');

      if (topItem === null) {
        topItem = item;
      }

      if (hash && activeItem === null && anchor === hash) {
        activeItem = item;
      }
    });

    // Default to the first tab being active.
    if (activeItem === null) {
      activeItem = topItem;
    }

    // Set active item as active.
    if (activeItem !== null) {
      $activeItem = $(activeItem);

      activeItem.setAttribute('data-tab-active', '');
      activeTabId = $activeItem.find('a').attr('href').substring(1);

      // Initialize Slider.
      updateSlider($tabSlider, $activeItem);
    }

    // Add window resize listener
    $(window).on('resize orientationchange', Drupal.ahsThemeSite.debounce(() => {
      windowResizeEvent(tabWidgetInstance);
    }, 50));

    tabLinks.forEach((link, i) => {
      let isActive = false;
      const anchor = link.getAttribute('href').split('#')[1];
      const attributes = {
        role: 'tab',
        'aria-controls': anchor,
      };

      // If it's the first element update the attributes
      if (activeTabId !== null && anchor === activeTabId) {
        isActive = true;
      }

      // Add the various accessibility roles and labels to the links
      Object.keys(attributes).forEach((key) => {
        if ({}.hasOwnProperty.call(attributes, key)) {
          link.setAttribute(key, attributes[key]);
        }
      });

      if (isActive) {
        selectLink(link);
      } else {
        unselectLink(link);
      }

      // Add next section link

      // Click Event Listener
      link.addEventListener('click', (e) => {
        e.preventDefault();
        tabClickEvent(e, tabLinks, tabPanels, tabSlider, i);
      });

      // Keyboard event listener
      link.addEventListener('keydown', (e) => {
        keyboardEvent(e, tabLinks, tabPanels, tabItems, i);
      });
    });

    // Mobile controls.
    tabAccordionControls.forEach((tabAccordionControl, i) => {
      const anchor = tabAccordionControl.getAttribute('href').split('#')[1];
      const id = tabAccordionControl.getAttribute('id');
      const attributes = {
        'aria-controls': anchor,
      };
      if (!id) {
        attributes.id = `tab-accordion-link-${i}`;
      }

      Object.keys(attributes).forEach((key) => {
        if ({}.hasOwnProperty.call(attributes, key)) {
          tabAccordionControl.setAttribute(key, attributes[key]);
        }
      });

      tabAccordionControl.addEventListener('click', (e) => {
        e.preventDefault();
        tabAccordionClickEvent(
          e,
          tabLinks,
          tabPanels,
          tabAccordionControls,
        );
      });
    });

    // Set up tab panels.
    tabPanels.forEach((panel, i) => {
      // Hide panel by default.
      let isActive = false;
      const attributes = {
        role: 'tabpanel',
        'aria-labelledby': `tab-${tabWidgetId}-tab-link-${i}`,
      };

      // Set up next tab link
      // nextTabLink.setAttribute('href', `#tab-link-${nextTabLinkIndex}`);
      // nextTabLink.textContent = 'Next Tab';
      // panel.appendChild(nextTabLink);

      if (activeTabId !== null && $(panel).attr('id') === activeTabId) {
        isActive = true;
        const activeTabControl = panel.previousElementSibling;
        if (activeTabControl != null && activeTabControl.classList.contains('tabs-accordion-control')) {
          activeTabControl.setAttribute('data-tab-active', '');
        }
      }

      Object.keys(attributes).forEach((key) => {
        if ({}.hasOwnProperty.call(attributes, key)) {
          panel.setAttribute(key, attributes[key]);
        }
      });

      if (isActive) {
        showPanel(panel);
      } else {
        hidePanel(panel);
      }
    });

    // This could trigger the 'ahsThemeTabSelected' event after the initial
    // setup and determination of the initial selected tab. However, the
    // tab-widget.js setup is ran in Drupal behavior which depends on the order
    // of the loaded modules and theme, ie the event would be triggered before
    // modules could add a listener.
    // Any initial setup should be handled in the module's Drupal behavior.
    // See src/modules/ahs_book_apt/js/ahs_book_apt.widget_facility_location.js.
  }

  Drupal.behaviors.themeTabWidget = {
    attach(context) {
      const tabWidgets = once('themeTabWidgetInit', '.js-tab-widget', context);
      if (tabWidgets.length) {
        tabWidgets.forEach((tabWidget) => {
          init(tabWidget);
        });
      }
    },
  };
}(jQuery, Drupal, once));
